<script setup lang="ts">
import { useKeyModifier } from "@vueuse/core";
import type { ICellRendererParams } from "ag-grid-community";
import { computed, nextTick, onUnmounted, ref, watch } from "vue";

import actions from "~/actions";
import { UNGROUPED_PSEUDO_GROUP_BY } from "~/common/groupBy";
import Checkbox from "~/components/dumb/Checkbox.vue";
import InputTitleEditor from "~/components/dumb/InputTitleEditor.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import TaskClickWrapper from "~/components/visualization/components/TaskClickWrapper.vue";
import { ChevronDownIcon } from "~/icons";
import {
  CommandId,
  DartboardKind,
  EditorMode,
  SubtaskDisplayMode,
  ViewKind,
  VisualizationItemType,
} from "~/shared/enums";
import type { VisualizationItemTask } from "~/shared/types";
import { useAppStore, usePageStore } from "~/stores";
import { useAsyncTask } from "~/utils/entityIncrementalLoading";

const props = defineProps<{
  params: ICellRendererParams<VisualizationItemTask> & {
    editorMode: EditorMode;
  };
}>();

const appStore = useAppStore();
const pageStore = usePageStore();

const shiftState = useKeyModifier("Shift");

const { task } = useAsyncTask(computed(() => props.params?.data?.duid));
const isPublicViewOrTrash = computed(() => pageStore.isPublicView || appStore.currentPage?.kind === ViewKind.TRASH);
const isFinishedDartboard = computed(() => appStore.currentPage?.kind === DartboardKind.FINISHED);

const isListMiniBaseMode = computed(() => props.params.editorMode === EditorMode.LIST_MINI);
const isListMiniTcmMode = computed(() => props.params.editorMode === EditorMode.LIST_MINI_TCM);

const editor = ref<InstanceType<typeof InputTitleEditor> | null>(null);

const getLevel = () => props.params.node.uiLevel - (appStore.groupBy === UNGROUPED_PSEUDO_GROUP_BY ? 0 : 1);
const getIsParent = () => props.params.node.allChildrenCount !== null;
const getIsExpanded = (isParent: boolean) => (isParent ? props.params.node.expanded : false);

let initialized = false;
const notFlat = computed(() => appStore.subtaskDisplayMode !== SubtaskDisplayMode.FLAT);
const isParent = ref(getIsParent());
const level = ref(getLevel());
const isExpanded = ref(getIsExpanded(isParent.value));

const isHovered = computed(
  () =>
    !!task.value &&
    appStore.hoverRow?.data?.itemType === VisualizationItemType.TASK &&
    task.value.duid === appStore.hoverRow.data.duid
);
const isSelected = computed(() => !!task.value && appStore.selectedTaskDuids.has(task.value?.duid));
const showCheckbox = computed(() => isHovered.value || isSelected.value);
const chevronHardHidden = computed(() => (isPublicViewOrTrash.value || isFinishedDartboard.value) && !isParent.value);
const showChevron = computed(() => isParent.value || isHovered.value || isSelected.value);

const updateHierarchy = async () => {
  await nextTick();
  const newIsParent = getIsParent();
  if (initialized && newIsParent && !isParent.value) {
    props.params.node.setExpanded(task.value?.expanded ?? true);
  } else {
    isExpanded.value = getIsExpanded(newIsParent);
  }
  isParent.value = newIsParent;
  level.value = getLevel();
  initialized = true;
};
updateHierarchy();

props.params.node.addEventListener("allChildrenCountChanged", updateHierarchy);
props.params.node.addEventListener("expandedChanged", updateHierarchy);
props.params.node.addEventListener("uiLevelChanged", updateHierarchy);
props.params.node.addEventListener("hasChildrenChanged", updateHierarchy);
onUnmounted(() => {
  props.params.node.removeEventListener("allChildrenCountChanged", updateHierarchy);
  props.params.node.removeEventListener("expandedChanged", updateHierarchy);
  props.params.node.removeEventListener("uiLevelChanged", updateHierarchy);
  props.params.node.removeEventListener("hasChildrenChanged", updateHierarchy);
});

watch([() => task.value?.expanded, () => appStore.collapsedGroups], () => {
  props.params.node.setExpanded(task.value?.expanded ?? false);
});

const leftPaddingPx = computed(() => {
  if ((pageStore.isMobile && !notFlat.value) || isListMiniBaseMode.value || isListMiniTcmMode.value) {
    return 0;
  }
  if (!notFlat.value) {
    return 4;
  }
  return level.value * 20;
});

const toggleSubtasks = () => {
  if (isParent.value) {
    props.params.node.setExpanded(!isExpanded.value);
    return;
  }

  if (isPublicViewOrTrash.value || isFinishedDartboard.value) {
    return;
  }

  isExpanded.value = true;
  props.params.node.setExpanded(true);
  actions.visualization.createSubtask(task.value, { noDetail: true });
};

const toggleSelected = (newValue: boolean) => {
  actions.visualization.selectWithClick(props.params.node, newValue, !!shiftState.value);
};

defineExpose({
  focus: () => editor.value?.focus(),
});
</script>

<template>
  <TaskClickWrapper v-if="task" :task="task" :editor-mode="params.editorMode">
    <template #default="args">
      <div
        v-bind="args"
        class="flex h-full items-center gap-1 pr-2"
        :class="{
          'pl-[17px]': isListMiniBaseMode,
          'pl-[22px]': isListMiniTcmMode,
          'pl-2': !(isListMiniBaseMode || isListMiniTcmMode),
        }">
        <Tooltip
          v-if="!pageStore.isMobile && !pageStore.isPublicView && !(isListMiniBaseMode || isListMiniTcmMode)"
          :class="!showCheckbox && 'opacity-0'"
          :command-id="isSelected ? CommandId.DESELECT_TASK : CommandId.SELECT_TASK">
          <Checkbox
            :value="isSelected"
            :label="isSelected ? 'Unselect task' : 'Select task'"
            class="!pointer-events-auto"
            @change="toggleSelected"
            @click.stop />
        </Tooltip>
        <div class="flex h-full min-w-0 grow items-center gap-1" :style="{ 'padding-left': `${leftPaddingPx}px` }">
          <Tooltip
            v-if="!(isListMiniBaseMode || isListMiniTcmMode) && notFlat && !pageStore.isMobile"
            :disabled="chevronHardHidden"
            :command-id="
              isParent ? (isExpanded ? CommandId.HIDE_SUBTASKS : CommandId.SHOW_SUBTASKS) : CommandId.CREATE_SUBTASKS
            "
            class="dart-subtask-expand rounded-sm opacity-0"
            :class="{
              'cursor-pointer opacity-100 hover:bg-opposite/10': showChevron && !chevronHardHidden,
            }">
            <button
              type="button"
              aria-label="Toggle subtasks"
              class="shrink-0 rounded-sm focus-ring-lt print:hidden"
              @click="toggleSubtasks">
              <ChevronDownIcon
                class="icon-sm text-vlt transition-transform"
                :class="[!isExpanded && '-rotate-90', isParent && '!text-lt']" />
            </button>
          </Tooltip>
          <InputTitleEditor
            ref="editor"
            :task="task"
            :value="task.title"
            :editor-mode="params.editorMode"
            :has-shown-parent="level > 0"
            :subtasks-expanded="isExpanded"
           
            @toggle-subtasks="toggleSubtasks" />
        </div>
      </div>
    </template>
  </TaskClickWrapper>
</template>
